import { useEffect, useMemo, useRef, useState } from "react";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { skipCountdownRedirectsName } from "@circle-react/helpers/paywallCustomizationHelpers";
import type { PaywallCustomization } from "@circle-react/types/PaywallCustomization";
import { formatCountdownTimer, getCookieFallback } from "../helpers";
import { parseCookie } from "../helpers";

export interface UseCountdownOptions {
  countdownProperties: PaywallCustomization["countdown_properties"];
}

const TIMER_INTERVAL = 1000;

export const useCountdown = ({ countdownProperties }: UseCountdownOptions) => {
  const { paywall, isProcessingPayment, isPerformingVerification } =
    usePaywallCheckoutContext();
  const isProcessing = useRef<boolean>(
    isProcessingPayment || isPerformingVerification,
  );

  const cookieData = useMemo(
    () => parseCookie(paywall?.id) || getCookieFallback(countdownProperties),
    [paywall?.id, countdownProperties],
  );
  const endDate = cookieData.finish_at;

  const [timeRemainingText, setTimeRemainingText] = useState("");
  const [exceedTime, setExceedTime] = useState<number>(0);

  useEffect(() => {
    isProcessing.current = isProcessingPayment || isPerformingVerification;
  }, [isProcessingPayment, isPerformingVerification]);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete(skipCountdownRedirectsName);
    window.history.replaceState({}, "", url);
  }, []);

  useEffect(() => {
    if (!endDate) {
      setTimeRemainingText("");
      return;
    }

    const updateTimer = () => {
      setTimeRemainingText(formatCountdownTimer(endDate, setExceedTime));
    };

    updateTimer();
    const interval = setInterval(updateTimer, TIMER_INTERVAL);

    return () => clearInterval(interval);
  }, [endDate]);

  return {
    timeRemainingText,
    exceedTime,
  };
};
